// @ts-nocheck
import type { HeadstoneDTO } from "../api/client";
import { holeIdFromCoords } from "./hole-id";
import { speciesDisplayLabel } from "./breeds";
import { formatDateForLocale, formatYearForLocale, yearsFromLifespan } from "../i18n/format";
import { t } from "../i18n";

export type LegacyHeadstone = {
  id: string;
  ownerId?: string | null;
  holeId: string;
  name: string;
  species?: string;
  breed?: string;
  birthDate?: string;
  deathDate?: string;
  /** Compact years for the tombstone face (e.g. "2011 – 2024"). */
  dateLabel: string;
  /** Full locale dates for the memorial sheet and catalog. */
  fullDateLabel: string;
  metaLabel: string;
  epitaph: string;
  photo: string | null;
  gallery?: { url: string; objectName: string; kind: 'photo' | 'video' }[];
  timestamp: number;
  paymentStatus: string;
  candleCount: number;
  flowerCount: number;
  styleKey: string;
};

const DATE_RANGE_RE = /^\s*\d{4}\s*[-–—]\s*\d{4}\s*$/;

function yearRangeLabel(birthYear: string, deathYear: string): string {
  return t("tombstone.dateRange", { birth: birthYear, death: deathYear });
}

function compactYearsFromLifespan(value: string): string {
  const years = yearsFromLifespan(value);
  if (years.length >= 2) return yearRangeLabel(years[0], years[years.length - 1]);
  if (years.length === 1) return years[0];
  return value.trim();
}

function buildDateLabel(pet: HeadstoneDTO["pet"]): string {
  const birth = pet.birthDate ?? undefined;
  const death = pet.deathDate ?? undefined;
  if (birth && death) {
    return yearRangeLabel(formatYearForLocale(birth), formatYearForLocale(death));
  }
  if (birth) return formatYearForLocale(birth);
  if (death) return formatYearForLocale(death);
  if (pet.lifespan) return compactYearsFromLifespan(pet.lifespan);
  if (pet.species && DATE_RANGE_RE.test(pet.species)) return pet.species.trim();
  return "";
}

function buildFullDateLabel(pet: HeadstoneDTO["pet"]): string {
  const birth = pet.birthDate ?? undefined;
  const death = pet.deathDate ?? undefined;
  if (birth && death) {
    return t("tombstone.dateRange", {
      birth: formatDateForLocale(birth),
      death: formatDateForLocale(death),
    });
  }
  if (birth) {
    return t("tombstone.bornOn", { date: formatDateForLocale(birth) });
  }
  if (death) {
    return t("tombstone.passedOn", { date: formatDateForLocale(death) });
  }
  if (pet.lifespan) return pet.lifespan.trim();
  if (pet.species && DATE_RANGE_RE.test(pet.species)) return pet.species.trim();
  return "";
}

function buildMetaLabel(species?: string | null, breed?: string | null): string {
  const parts: string[] = [];
  if (species && !DATE_RANGE_RE.test(species)) {
    parts.push(speciesDisplayLabel(species));
  }
  if (breed) parts.push(breed);
  return parts.join(" · ");
}

function cleanEpitaph(
  epitaph: string | null | undefined,
  dateLabels: string[],
  legacySpecies?: string | null,
): string {
  const raw = (epitaph ?? "").trim();
  if (!raw) return "";
  if (dateLabels.some((label) => label && raw === label)) return "";
  if (legacySpecies && raw === legacySpecies) return "";
  return raw;
}

export function dtoToLegacy(dto: HeadstoneDTO): LegacyHeadstone {
  const pet = dto.pet;
  const dateLabel = buildDateLabel(pet);
  const fullDateLabel = buildFullDateLabel(pet);
  const metaLabel = buildMetaLabel(pet.species, pet.breed);
  const epitaph = cleanEpitaph(dto.epitaph, [dateLabel, fullDateLabel], pet.species);

  return {
    id: dto.id,
    ownerId: dto.ownerId,
    holeId: holeIdFromCoords(dto.x, dto.y),
    name: pet.name,
    species: pet.species ?? undefined,
    breed: pet.breed ?? undefined,
    birthDate: pet.birthDate ?? undefined,
    deathDate: pet.deathDate ?? undefined,
    dateLabel,
    fullDateLabel,
    metaLabel,
    epitaph,
    photo: pet.imageUrl ?? null,
    gallery: Array.isArray(pet.galleryUrls) ? pet.galleryUrls : [],
    timestamp: new Date(dto.createdAt).getTime(),
    paymentStatus: dto.paymentStatus,
    candleCount: dto.candleCount ?? 0,
    flowerCount: dto.flowerCount ?? 0,
    styleKey: dto.styleKey ?? 'standard',
  };
}
